/**
 * Utility functions for URL handling in hash router blog
 * For hash router format: /#/blog/#post-[postId]
 */

/**
 * Extracts post ID from the URL hash with hash router support
 * @returns {string|null} Post ID or null if not found
 */
export function extractPostIdFromUrl() {
  const hash = window.location.hash; // Gets the hash portion including #/blog
  
  // Check for post hash pattern
  const postHashRegex = /#post-([^/&#]+)/;
  const match = hash.match(postHashRegex);
  
  if (match && match[1]) {
    return match[1]; // Return the captured post ID
  }
  
  return null;
}

/**
 * Constructs a URL for a blog post with correct hash router format
 * @param {string} postId - ID of the post
 * @returns {string} URL with proper hash format for hash router
 */
export function constructBlogPostUrl(postId) {
  // Get the base path (before any secondary hash)
  const currentUrl = window.location.href;
  const baseUrl = currentUrl.split('#post-')[0];
  
  // Check if we're already on the blog page
  if (baseUrl.includes('/#/blog')) {
    return `${baseUrl}#post-${postId}`;
  } else {
    // We need to create a full path to the blog with the post hash
    const origin = window.location.origin;
    return `${origin}/#/blog#post-${postId}`;
  }
}

/**
 * Normalizes URL to prevent duplication issues
 * 
 * @param {string} url - URL to normalize
 * @returns {string} - Normalized URL
 */
export function normalizeUrl(url) {
  // If there are multiple occurrences of '/blog/', keep only one
  if (url.includes('/blog/')) {
    // Use regex to find all occurrences of '/blog/'
    const matches = url.match(/\/blog\//g);
    if (matches && matches.length > 1) {
      // Replace all but the first occurrence
      let normalized = url;
      for (let i = 1; i < matches.length; i++) {
        normalized = normalized.replace('/blog/', '/');
      }
      return normalized;
    }
  }
  return url;
}

/**
 * Updates browser URL safely for blog posts in hash router context
 * @param {string} postId - ID of the post
 */
export function updateUrlForPost(postId) {
  // Create URL for blog posts with correct hash router format
  const newUrl = constructBlogPostUrl(postId);
  
  // Use replaceState to update URL without navigation
  window.history.replaceState(null, '', newUrl);
  
  // Save to sessionStorage for refresh handling
  sessionStorage.setItem('lastViewedPost', postId);
}
